/**
 * Step-by-step wizard to create a user, give them a role + scope, set a
 * reporting line, and a position — all in one flow. If the place they
 * should be assigned to (constituency / ward / branch) doesn't exist yet,
 * it can be created inline without leaving the wizard.
 *
 * Steps:
 *   1. Basics       — email, full name, password
 *   2. Where        — constituency → ward → branch (with "+ create" inline)
 *   3. Role         — pick from existing roles + reports-to
 *   4. Position     — optional metadata
 *   5. Review       — confirm + submit (creates user + assignment in order)
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ApiError } from "../../api/client";
import { enrollFromFile, toArray } from "../../auth/face";
import { HierarchyApi, type Branch, type Constituency, type District, type Ward } from "../hierarchy/api";
import { TagInput, COMMON_SPECIALISATIONS } from "../../shell/TagInput";
import { RbacApi, type AppUser, type Position, type Role } from "./api";

type ScopeLevel = "district" | "constituency" | "ward" | "branch";

interface FormState {
  // Step 1
  email: string;
  fullName: string;
  password: string;
  // Step 2
  scopeLevel: ScopeLevel;
  did: string;
  cid: string;
  wid: string;
  bid: string;
  // Step 3
  roleId: string;
  reportsToUid: string;
  // Step 4
  positionId: string;
  // Step 5 — personal specialisations layered on top of the role's tags
  specialisations: string[];
  // Step 6 — face descriptor (128 floats) computed in-browser from a photo the
  // admin uploads. null = no facial second factor enrolled for this officer.
  faceDescriptor: number[] | null;
  // The photo itself (base64 JPEG), encrypted + stored server-side on submit.
  faceImage: string | null;
  faceImageContentType: string | null;
}

const INITIAL: FormState = {
  email: "",
  fullName: "",
  password: "",
  scopeLevel: "branch",
  did: "",
  cid: "",
  wid: "",
  bid: "",
  roleId: "",
  reportsToUid: "",
  positionId: "",
  specialisations: [],
  faceDescriptor: null,
  faceImage: null,
  faceImageContentType: null,
};

interface StepDef { key: string; title: string; }
const STEPS: StepDef[] = [
  { key: "basics", title: "Basics" },
  { key: "where", title: "Where" },
  { key: "role", title: "Role" },
  { key: "position", title: "Position" },
  { key: "specialisations", title: "Specialisations" },
  { key: "face", title: "Face" },
  { key: "review", title: "Review" },
];

export function NewUserWizard() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [error, setError] = useState<string | null>(null);
  const [createdInfo, setCreatedInfo] = useState<{ uid: string; email: string } | null>(null);

  const update = (patch: Partial<FormState>) => setForm((s) => ({ ...s, ...patch }));

  const districts = useQuery({ queryKey: ["districts"], queryFn: HierarchyApi.listDistricts });
  const constituencies = useQuery({
    queryKey: ["constituencies", form.did],
    queryFn: () => form.did ? HierarchyApi.listConstituenciesInDistrict(form.did) : HierarchyApi.listConstituencies(),
  });
  const wards = useQuery({
    queryKey: ["wards", form.cid],
    queryFn: () => HierarchyApi.listWards(form.cid),
    enabled: !!form.cid,
  });
  const branches = useQuery({
    queryKey: ["branches", form.wid],
    queryFn: () => HierarchyApi.listBranches(form.wid),
    enabled: !!form.wid,
  });
  const roles = useQuery({ queryKey: ["roles"], queryFn: RbacApi.listRoles });
  const positions = useQuery({ queryKey: ["positions"], queryFn: RbacApi.listPositions });
  const users = useQuery({ queryKey: ["users"], queryFn: RbacApi.listUsers });

  const submit = useMutation({
    mutationFn: async () => {
      // 1. Create the user with their personal specialisations.
      const created = await RbacApi.inviteUser({
        email: form.email,
        fullName: form.fullName,
        password: form.password,
        specialisations: form.specialisations,
        faceDescriptor: form.faceDescriptor,
        faceImage: form.faceImage,
        faceImageContentType: form.faceImageContentType,
      });
      // 2. Create assignment if a role + scope have been selected
      if (form.roleId) {
        const scopeId =
          form.scopeLevel === "branch" ? form.bid :
          form.scopeLevel === "ward" ? form.wid :
          form.scopeLevel === "constituency" ? form.cid : form.did;
        if (scopeId) {
          await RbacApi.createAssignment({
            userId: created.uid,
            roleId: form.roleId,
            positionId: form.positionId || null,
            scopeLevel: form.scopeLevel,
            scopeId,
            reportsToUid: form.reportsToUid || null,
            startsAt: null,
            endsAt: null,
          });
        }
      }
      return created;
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["assignments"] });
      setCreatedInfo({ uid: created.uid, email: created.email });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });

  const total = STEPS.length;
  const next = () => setStep((s) => Math.min(s + 1, total));
  const back = () => setStep((s) => Math.max(1, s - 1));

  // After successful create, show a summary page.
  if (createdInfo) {
    const role = roles.data?.items.find((r) => r.id === form.roleId);
    const scopeLabel =
      form.scopeLevel === "branch" ? "branch" :
      form.scopeLevel === "ward" ? "ward" :
      form.scopeLevel === "constituency" ? "constituency" : "district";
    return (
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: "0 0 4px" }}>User created and assigned</h1>
        <p className="muted" style={{ margin: "0 0 20px", fontSize: 14 }}>
          They can sign in with the credentials below. Their access is already wired up — no separate Assignments step needed.
        </p>
        <div className="card">
          <dl className="kv">
            <dt>Email</dt><dd><code>{createdInfo.email}</code></dd>
            <dt>Password</dt><dd><code>{form.password}</code></dd>
            <dt>Sign-in URL</dt><dd><code>https://winnersden-55816.web.app/portal</code></dd>
            <dt>Role</dt><dd>{role ? role.name : "—"}</dd>
            <dt>Scope</dt><dd>Assigned at the {scopeLabel} level</dd>
          </dl>
          <p className="muted" style={{ fontSize: 13 }}>
            Tell them they can change their password later by signing in and clicking Reset password
            on their profile, or by you using "Reset password" from the Users list.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={() => nav("/app/admin/users", { replace: true })}>Done</button>
          <button className="ghost" onClick={() => { setCreatedInfo(null); setForm(INITIAL); setStep(1); setError(null); }}>
            Create another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Create a user</h1>
        <Link to="/app/admin/users" className="muted" style={{ fontSize: 13 }}>Cancel</Link>
      </div>
      <p className="muted" style={{ margin: "0 0 18px", fontSize: 14 }}>
        Set their email, password, and where they work. We'll walk it step by step.
      </p>

      <Progress current={step} total={total} labels={STEPS.map((s) => s.title)} />
      {error && <div className="error">{error}</div>}

      <div className="card" style={{ padding: 20 }}>
        {step === 1 && (
          <Step1 form={form} update={update} onNext={next} />
        )}
        {step === 2 && (
          <Step2Where
            form={form} update={update}
            districts={districts.data?.items ?? []}
            constituencies={constituencies.data?.items ?? []}
            wards={wards.data?.items ?? []}
            branches={branches.data?.items ?? []}
            onNext={next} onBack={back}
            onCreated={() => {
              qc.invalidateQueries({ queryKey: ["districts"] });
              qc.invalidateQueries({ queryKey: ["constituencies", form.did] });
              qc.invalidateQueries({ queryKey: ["wards", form.cid] });
              qc.invalidateQueries({ queryKey: ["branches", form.wid] });
            }}
          />
        )}
        {step === 3 && (
          <Step3Role
            form={form} update={update}
            roles={roles.data?.items ?? []}
            users={(users.data?.items ?? []).filter((u) => u.email !== form.email)}
            onNext={next} onBack={back}
          />
        )}
        {step === 4 && (
          <Step4Position
            form={form} update={update}
            positions={(positions.data?.items ?? []).filter((p) => p.scopeLevel === form.scopeLevel)}
            onNext={next} onBack={back}
          />
        )}
        {step === 5 && (
          <Step5Specialisations form={form} update={update} onNext={next} onBack={back} />
        )}
        {step === 6 && (
          <Step6Face form={form} update={update} onNext={next} onBack={back} />
        )}
        {step === 7 && (
          <Step7Review
            form={form}
            roles={roles.data?.items ?? []}
            positions={positions.data?.items ?? []}
            users={users.data?.items ?? []}
            busy={submit.isPending}
            onBack={back}
            onSubmit={() => { setError(null); submit.mutate(); }}
          />
        )}
      </div>
    </div>
  );
}

// -- Steps -----------------------------------------------------------------

function checkPasswordStrength(pw: string, email: string, fullName: string): string | null {
  if (pw.length < 12) return "At least 12 characters";
  if (!/[A-Za-z]/.test(pw)) return "Needs at least one letter";
  if (!/\d/.test(pw)) return "Needs at least one digit";
  if (!/[^A-Za-z0-9\s]/.test(pw)) return "Needs at least one symbol (e.g. ! @ # $ %)";
  const local = email.split("@")[0]?.toLowerCase() ?? "";
  if (local.length >= 4 && pw.toLowerCase().includes(local)) return "Must not contain the email address";
  if (fullName && pw.toLowerCase().includes(fullName.toLowerCase())) return "Must not contain the user's name";
  return null;
}

function Step1({ form, update, onNext }: { form: FormState; update: (p: Partial<FormState>) => void; onNext: () => void }) {
  const pwError = form.password ? checkPasswordStrength(form.password, form.email, form.fullName) : null;
  const ready = !!form.email && !!form.fullName && !!form.password && !pwError;
  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>Their basics</h2>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>
        Set what they sign in with. You'll share this with them out-of-band.
      </p>
      <Field label="Email">
        <input type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} placeholder="them@example.com" required />
      </Field>
      <Field label="Full name">
        <input value={form.fullName} onChange={(e) => update({ fullName: e.target.value })} placeholder="e.g. Joseph Banda" required />
      </Field>
      <Field
        label="Password"
        hint={
          pwError
            ? `❌ ${pwError}`
            : form.password
              ? "✓ Strong enough."
              : "Min 12 characters. Must contain a letter, a digit and a symbol. Must not contain the email or name."
        }
      >
        <input
          type="text"
          value={form.password}
          onChange={(e) => update({ password: e.target.value })}
          minLength={12}
          placeholder="A starter password"
          required
        />
      </Field>
      <NavRow onBack={null} onNext={onNext} canNext={ready} hint={pwError ?? "Fill all fields with a strong password to continue"} />
    </div>
  );
}

function Step2Where({
  form, update, districts, constituencies, wards, branches, onNext, onBack, onCreated,
}: {
  form: FormState;
  update: (p: Partial<FormState>) => void;
  districts: District[];
  constituencies: Constituency[];
  wards: Ward[];
  branches: Branch[];
  onNext: () => void;
  onBack: () => void;
  onCreated: () => void;
}) {
  const ready = (
    (form.scopeLevel === "district" && !!form.did) ||
    (form.scopeLevel === "constituency" && !!form.cid) ||
    (form.scopeLevel === "ward" && !!form.wid) ||
    (form.scopeLevel === "branch" && !!form.bid)
  );

  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>Where do they work?</h2>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>
        Pick a district, constituency, ward, or branch. If it doesn't exist yet, click <strong>+ create</strong> to add it.
      </p>

      <Field label="Scope level" hint="Branch is the smallest unit; district is the broadest.">
        <select value={form.scopeLevel} onChange={(e) => update({ scopeLevel: e.target.value as ScopeLevel, cid: "", wid: "", bid: "" })}>
          <option value="branch">Branch</option>
          <option value="ward">Ward</option>
          <option value="constituency">Constituency</option>
          <option value="district">District</option>
        </select>
      </Field>

      <PickerWithCreate
        label="District"
        items={districts}
        value={form.did}
        onChange={(id) => update({ did: id, cid: "", wid: "", bid: "" })}
        creator={{
          parentLabel: null,
          fields: [
            { key: "name", label: "Name", placeholder: "e.g. Lusaka" },
            { key: "code", label: "Short code", placeholder: "Leave blank to auto-fill", slugify: true },
          ],
          create: async (vals) => {
            const d = await HierarchyApi.createDistrict({ name: vals.name, code: vals.code });
            update({ did: d.id, cid: "", wid: "", bid: "" });
            onCreated();
          },
        }}
      />

      {form.scopeLevel !== "district" && (
        <PickerWithCreate
          label="Constituency"
          items={constituencies}
          value={form.cid}
          onChange={(id) => update({ cid: id, wid: "", bid: "" })}
          disabled={!form.did}
          disabledHint="Pick a district above first — constituencies belong to a district"
          creator={form.did ? {
            parentLabel: `in ${districts.find((d) => d.id === form.did)?.name ?? "this district"}`,
            fields: [
              { key: "name", label: "Name", placeholder: "e.g. Mansa Central" },
              { key: "code", label: "Short code", placeholder: "Leave blank to auto-fill", hint: "Spaces become hyphens; punctuation is dropped.", slugify: true },
            ],
            create: async (vals) => {
              const c = await HierarchyApi.createConstituency({ name: vals.name, code: vals.code, districtId: form.did });
              update({ cid: c.id, wid: "", bid: "" });
              onCreated();
            },
          } : undefined}
        />
      )}

      {(form.scopeLevel === "ward" || form.scopeLevel === "branch") && (
        <PickerWithCreate
          label="Ward"
          items={wards}
          value={form.wid}
          onChange={(id) => update({ wid: id, bid: "" })}
          disabled={!form.cid}
          disabledHint="Pick a constituency above first — wards always belong to a constituency"
          creator={form.cid ? {
            parentLabel: `in ${constituencies.find((c) => c.id === form.cid)?.name ?? "this constituency"}`,
            fields: [
              { key: "name", label: "Name", placeholder: "e.g. Ward 7" },
              { key: "code", label: "Short code", placeholder: "Leave blank to auto-fill", slugify: true },
            ],
            create: async (vals) => {
              const w = await HierarchyApi.createWard(form.cid, { name: vals.name, code: vals.code });
              update({ wid: w.id, bid: "" });
              onCreated();
            },
          } : undefined}
        />
      )}

      {form.scopeLevel === "branch" && (
        <PickerWithCreate
          label="Branch"
          items={branches}
          value={form.bid}
          onChange={(id) => update({ bid: id })}
          disabled={!form.wid}
          disabledHint="Pick a ward above first — branches always belong to a ward"
          creator={form.wid ? {
            parentLabel: `in ${wards.find((w) => w.id === form.wid)?.name ?? "this ward"}`,
            fields: [
              { key: "name", label: "Name", placeholder: "e.g. Main Market Branch" },
              { key: "code", label: "Short code", placeholder: "Leave blank to auto-fill", slugify: true },
            ],
            create: async (vals) => {
              const b = await HierarchyApi.createBranch(form.wid, { name: vals.name, code: vals.code });
              update({ bid: b.id });
              onCreated();
            },
          } : undefined}
        />
      )}

      <NavRow onBack={onBack} onNext={onNext} canNext={ready} hint="Pick or create an area to continue" />
    </div>
  );
}

function Step3Role({
  form, update, roles, users, onNext, onBack,
}: {
  form: FormState;
  update: (p: Partial<FormState>) => void;
  roles: Role[];
  users: AppUser[];
  onNext: () => void;
  onBack: () => void;
}) {
  const ready = !!form.roleId;
  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>What can they do?</h2>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>
        Pick a role. The role decides which screens and actions they have. Without one, they sign in to a blank app —
        so this is required. You can change it later by clicking <strong>Edit access</strong> on their row in
        <Link to="/app/admin/users"> Users</Link>, and edit what each role grants in <Link to="/app/admin/roles">Roles</Link>.
      </p>

      <Field label="Role" hint={form.roleId
        ? `${roles.find((r) => r.id === form.roleId)?.permissions.length ?? 0} permission(s) included`
        : "Required — pick the role that matches what this person should be able to do."}>
        <select value={form.roleId} onChange={(e) => update({ roleId: e.target.value })}>
          <option value="">— pick a role —</option>
          {roles.filter((r) => !r.isSystem || r.id === "superadmin").map((r) => (
            <option key={r.id} value={r.id}>{r.name}{r.isSystem ? " (system)" : ""}</option>
          ))}
        </select>
      </Field>

      <Field label="Reports to (optional)" hint="Used for the chain-of-command. Tasks can only flow downward through this chain.">
        <select value={form.reportsToUid} onChange={(e) => update({ reportsToUid: e.target.value })}>
          <option value="">(no one — top of their chain)</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>)}
        </select>
      </Field>

      <NavRow onBack={onBack} onNext={onNext} canNext={ready} hint="Pick a role to continue" />
    </div>
  );
}

function Step4Position({
  form, update, positions, onNext, onBack,
}: {
  form: FormState;
  update: (p: Partial<FormState>) => void;
  positions: Position[];
  onNext: () => void;
  onBack: () => void;
}) {
  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>Their title (optional)</h2>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>
        A position is a job title. It doesn't grant any extra power — just labels them in reports.
      </p>
      {positions.length === 0 ? (
        <div className="empty" style={{ padding: 18 }}>
          No positions defined for the <strong>{form.scopeLevel}</strong> level yet.
          You can add some in <Link to="/app/admin/positions">Positions</Link> later. Skip for now.
        </div>
      ) : (
        <Field label="Position">
          <select value={form.positionId} onChange={(e) => update({ positionId: e.target.value })}>
            <option value="">(no title)</option>
            {positions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      )}
      <NavRow onBack={onBack} onNext={onNext} canNext={true} />
    </div>
  );
}

function Step5Specialisations({
  form, update, onNext, onBack,
}: {
  form: FormState;
  update: (p: Partial<FormState>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>Specialisations</h2>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>
        Personal skill tags. Layered on top of the role's tags. The task wizard uses these to
        suggest people for a task that needs a specific skill (e.g. "surveillance").
      </p>
      <TagInput
        value={form.specialisations}
        onChange={(next) => update({ specialisations: next })}
        placeholder="e.g. surveillance, logistics — Enter to add"
        suggestions={COMMON_SPECIALISATIONS}
      />
      <NavRow onBack={onBack} onNext={onNext} canNext={true} />
    </div>
  );
}

function Step6Face({
  form, update, onNext, onBack,
}: {
  form: FormState;
  update: (p: Partial<FormState>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-picked after a failure
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setStatus({ kind: "err", text: "Please choose an image file." });
      return;
    }
    setBusy(true);
    setStatus(null);
    update({ faceDescriptor: null, faceImage: null, faceImageContentType: null });
    setPreview(null);
    try {
      const { descriptor, image, contentType, dataUrl } = await enrollFromFile(file);
      if (!descriptor) {
        setStatus({
          kind: "err",
          text: "Couldn't find a single clear face. Use a front-facing, well-lit photo of just this person.",
        });
      } else {
        setPreview(dataUrl);
        update({
          faceDescriptor: toArray(descriptor),
          faceImage: image,
          faceImageContentType: contentType,
        });
        setStatus({
          kind: "ok",
          text: "✓ Face captured. The photo is encrypted on save; they'll confirm with their device camera at sign-in.",
        });
      }
    } catch {
      setStatus({ kind: "err", text: "Couldn't process that image. Try a JPG or PNG." });
    } finally {
      setBusy(false);
    }
  }

  function clearPhoto() {
    setPreview(null);
    setStatus(null);
    update({ faceDescriptor: null, faceImage: null, faceImageContentType: null });
  }

  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>Face sign-in (optional)</h2>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>
        Attach a clear, front-facing photo of this officer. A face signature is computed
        <strong> in your browser</strong> for matching, and the photo is <strong>encrypted</strong> and
        stored securely (only a superadmin can ever view it). At sign-in they'll confirm with their
        device camera as a second step after their password. Leave blank to use password only.
      </p>

      <div className="face-upload">
        <div className="face-upload-preview">
          {preview ? <img src={preview} alt="Officer face preview" /> : <span className="muted" style={{ fontSize: 12 }}>No photo</span>}
        </div>
        <div className="face-upload-controls">
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}>
            {busy ? "Reading…" : form.faceDescriptor ? "Choose a different photo" : "Choose photo"}
          </button>
          {preview && (
            <button type="button" className="ghost" onClick={clearPhoto} disabled={busy}>Remove</button>
          )}
          <input ref={inputRef} type="file" accept="image/*" onChange={onFile} style={{ display: "none" }} />
        </div>
      </div>

      {status && (
        <div style={{ fontSize: 12, marginTop: 10, color: status.kind === "err" ? "var(--danger)" : "var(--accent)" }}>
          {status.text}
        </div>
      )}

      <NavRow onBack={onBack} onNext={onNext} canNext={!busy} hint={busy ? "Processing photo…" : undefined} />
    </div>
  );
}

function Step7Review({
  form, roles, positions, users, busy, onBack, onSubmit,
}: {
  form: FormState;
  roles: Role[];
  positions: Position[];
  users: AppUser[];
  busy: boolean;
  onBack: () => void;
  onSubmit: () => void;
}) {
  const role = roles.find((r) => r.id === form.roleId);
  const position = positions.find((p) => p.id === form.positionId);
  const reportsTo = users.find((u) => u.id === form.reportsToUid);
  return (
    <div>
      <h2 style={{ fontSize: 16, margin: "0 0 4px", fontWeight: 600 }}>Review</h2>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 13 }}>One last look. Click Create when ready.</p>
      <dl className="kv">
        <dt>Email</dt><dd>{form.email}</dd>
        <dt>Full name</dt><dd>{form.fullName}</dd>
        <dt>Starter password</dt><dd><code>{form.password}</code></dd>
        <dt>Where</dt>
        <dd>
          {form.scopeLevel === "branch" && `Branch ${form.bid}`}
          {form.scopeLevel === "ward" && `Ward ${form.wid}`}
          {form.scopeLevel === "constituency" && `Constituency ${form.cid}`}
        </dd>
        <dt>Role</dt><dd>{role ? role.name : "(none — they won't see anything yet)"}</dd>
        {position && (<><dt>Position</dt><dd>{position.name}</dd></>)}
        {reportsTo && (<><dt>Reports to</dt><dd>{reportsTo.fullName} ({reportsTo.email})</dd></>)}
        <dt>Face sign-in</dt>
        <dd>{form.faceDescriptor ? "Enrolled — verified by camera at sign-in" : "Not set (password only)"}</dd>
      </dl>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
        <button className="ghost" onClick={onBack} disabled={busy}>Back</button>
        <button onClick={onSubmit} disabled={busy}>{busy ? "Creating…" : "Create user"}</button>
      </div>
    </div>
  );
}

// -- Reusable bits ----------------------------------------------------------

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div style={{ marginTop: 10 }}>
      <label>{label}</label>
      {children}
      {hint && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function NavRow({
  onBack, onNext, canNext, hint,
}: { onBack: (() => void) | null; onNext: () => void; canNext: boolean; hint?: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18 }}>
      {onBack ? <button className="ghost" onClick={onBack}>Back</button> : <span />}
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        {!canNext && hint && <span className="muted" style={{ fontSize: 12 }}>{hint}</span>}
        <button onClick={onNext} disabled={!canNext}>Next</button>
      </div>
    </div>
  );
}

function Progress({ current, total, labels }: { current: number; total: number; labels: string[] }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 8, fontSize: 12, color: "var(--muted)", marginBottom: 6 }}>
        Step {current} of {total} <span>·</span> <strong style={{ color: "var(--text)" }}>{labels[current - 1]}</strong>
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {labels.map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: i < current ? "var(--accent)" : "var(--border)",
          }} />
        ))}
      </div>
    </div>
  );
}

interface CreatorField { key: string; label: string; placeholder?: string; hint?: string; slugify?: boolean; }

function slugify(s: string): string {
  return s.trim().replace(/\s+/g, "-").replace(/[^A-Za-z0-9_-]/g, "").slice(0, 32);
}

interface CreatorSpec {
  /** Shown above the create form so the user sees the parent context.
   *  null = top-level (no parent). */
  parentLabel: string | null;
  fields: CreatorField[];
  create: (vals: Record<string, string>) => Promise<void>;
}

function PickerWithCreate<T extends { id: string; name: string; code?: string }>({
  label, items, value, onChange, disabled, disabledHint, creator,
}: {
  label: string;
  items: T[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  disabledHint?: string;
  creator?: CreatorSpec;
}) {
  const [creating, setCreating] = useState(false);
  const [vals, setVals] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!creator) return;
    setBusy(true); setErr(null);
    // Auto-clean fields the creator marked sluggable. If a `code` field is
    // empty, derive it from `name`.
    const cleaned: Record<string, string> = { ...vals };
    for (const f of creator.fields) {
      if (f.slugify) {
        cleaned[f.key] = slugify(cleaned[f.key] || "") || slugify(cleaned["name"] || "");
      }
    }
    try {
      await creator.create(cleaned);
      setCreating(false);
      setVals({});
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 12 }}>
      <label>{label}</label>
      <div style={{ display: "flex", gap: 8 }}>
        <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
          <option value="">{disabled ? "— locked —" : "— pick —"}</option>
          {items.map((it) => <option key={it.id} value={it.id}>{it.name}{it.code ? ` (${it.code})` : ""}</option>)}
        </select>
        {creator && !disabled && (
          <button type="button" className="ghost" onClick={() => setCreating((v) => !v)} style={{ flexShrink: 0 }}>
            {creating ? "Cancel" : "+ create"}
          </button>
        )}
      </div>

      {disabled && disabledHint && (
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>↑ {disabledHint}</div>
      )}

      {creating && creator && (
        <form onSubmit={submit} style={{ marginTop: 8, padding: 14, border: "1px dashed var(--border)", borderRadius: "var(--radius-sm)", background: "var(--accent-soft)" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 4 }}>
            New {label.toLowerCase()}
            {creator.parentLabel && (
              <span style={{ color: "var(--accent)", fontWeight: 500 }}> {creator.parentLabel}</span>
            )}
          </div>
          {creator.parentLabel && (
            <div className="muted" style={{ fontSize: 11, marginBottom: 10 }}>
              This {label.toLowerCase()} will be permanently linked to its parent.
            </div>
          )}
          {err && <div className="error" style={{ marginBottom: 8 }}>{err}</div>}
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${creator.fields.length}, 1fr)`, gap: 8 }}>
            {creator.fields.map((f) => (
              <div key={f.key}>
                <label style={{ fontSize: 12, color: "var(--muted)" }}>{f.label}</label>
                <input
                  value={vals[f.key] || ""}
                  onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  required
                />
                {f.hint && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{f.hint}</div>}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button type="submit" disabled={busy}>{busy ? "Creating…" : "Add"}</button>
            <button type="button" className="ghost" onClick={() => setCreating(false)} disabled={busy}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}
